import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { vars } from 'src/core/themes';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  groupPrioritySort,
  useGroupSortingCriteria,
} from 'src/modals/GroupListings/components/ListingGroupInput.hook';
import { ColumnHeader } from 'src/tables/Table/components';
import { sortSeatingSeats } from 'src/utils/tableUtils';
import {
  Feature,
  Listing,
  ListingGroupItemInput,
  ListingGroupUndercutSetting,
  MergeListingGroupInput,
} from 'src/WebApiController';

import { useSetListingGroupValue } from '../../GroupListingsV2.hooks';
import { GroupListingsFormProps } from '../../GroupListingsV2.types';
import {
  GroupListingTableColumnId,
  UngroupedListingGroupId,
  usePreviewTableColumns,
} from './GroupListingsPreviewTable.constants';
import { withGroupUndercustSettings } from './GroupsPreview.utils';
import { MoveListingGroupDialog } from './MoveListingGroupDialog';

export type GroupListingTableItem = ListingGroupItemInput & Listing;

type GroupListingPreviewTableProps = {
  listingGroup: MergeListingGroupInput;
  eventListings: Listing[];
  isUngroupedGroup: boolean;
};

// Row Component
const DraggableRow = ({ row }: { row: Row<GroupListingTableItem | null> }) => {
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row?.original?.id ?? 0,
  });

  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform), //let dnd-kit do its thing
        transition: transition,
        opacity: isDragging ? 0.8 : 1,
        zIndex: isDragging ? 1 : 0,
        position: 'relative',
        lineHeight: vars.spacing['3xl'],
      }}
    >
      {row.getVisibleCells().map((cell) => (
        <td key={cell.id} style={{ width: cell.column.getSize() }}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
    </tr>
  );
};

const ColumnIdsAllowSorting = [
  GroupListingTableColumnId.Section,
  GroupListingTableColumnId.Row,
  GroupListingTableColumnId.Seats,
  GroupListingTableColumnId.AvailableQuantity,
  GroupListingTableColumnId.UnitCost,
  GroupListingTableColumnId.DeliveryType,
];

export const GroupListingPreviewTable: React.FC<
  GroupListingPreviewTableProps
> = ({ listingGroup, eventListings, isUngroupedGroup }) => {
  const { watch, setValue } = useFormContext<GroupListingsFormProps>();
  const listingGroups = watch('mergeListingGroupInputs');

  const [targetListingId, setTargetListingId] = useState<number>();
  const [sorting, setSorting] = useState<SortingState>([]);

  const moveListingGroupDialog = useBasicDialog();
  const idToSortingCriteria = useGroupSortingCriteria(eventListings);

  const setListingGroupValue = useSetListingGroupValue(
    listingGroup.listingGroupId
  );

  const { venueMapInfo } = useEventMapContext();
  const rowIdToOrdinalMap = useMemo(
    () =>
      venueMapInfo?.sections.reduce(
        (acc, section) => {
          section.rows.forEach((row) => {
            if (row.id != null && row.ordinal != null) {
              acc[row.id] = row.ordinal;
            }
          });
          return acc;
        },
        {} as Record<number, number>
      ),
    [venueMapInfo?.sections]
  );

  const toSortedGroupItems = useCallback(
    (groupItems: ListingGroupItemInput[]): ListingGroupItemInput[] => {
      return withGroupUndercustSettings(
        listingGroup.groupUndercutSetting,
        groupItems
      )
        .toSorted(
          groupPrioritySort(
            idToSortingCriteria,
            listingGroup.deprioritizedQuantities
          )
        )
        .map((listing, index) => ({
          ...listing,
          priority: index + 1,
        }));
    },
    [
      idToSortingCriteria,
      listingGroup.deprioritizedQuantities,
      listingGroup.groupUndercutSetting,
    ]
  );

  const hasTableSortInGroupingWizardFeature = useUserHasFeature(
    Feature.TableSortInGroupingWizard
  );

  const idToListing = useMemo(
    () =>
      eventListings.reduce(
        (res, listing) => {
          res[listing.id] = listing;
          return res;
        },
        {} as Record<number, Listing>
      ),
    [eventListings]
  );

  const listings = useMemo((): GroupListingTableItem[] => {
    return listingGroup.listingGroupItems
      .filter(({ listingId, priority }) => listingId > 0 && priority >= 0)
      .map((item) => {
        const listing = idToListing[item.listingId] ?? {};
        return {
          ...listing,
          ...item,
        };
      })
      .toSorted((a, b) => a.priority - b.priority);
  }, [idToListing, listingGroup.listingGroupItems]);

  const listingIds = useMemo<UniqueIdentifier[]>(
    () => listings.map(({ id }) => id),
    [listings]
  );

  const onMoveListing = useCallback(
    (listingId: number) => {
      setTargetListingId(listingId);
      moveListingGroupDialog.launchDialog();
    },
    [moveListingGroupDialog]
  );

  const onRemoveListingFromGroup = useCallback(
    (listingId: number) => {
      const updatedGroupItems = listingGroup.listingGroupItems.filter(
        (item) => item.listingId !== listingId
      );
      setListingGroupValue({
        listingGroupItems: toSortedGroupItems(updatedGroupItems),
      });
    },
    [listingGroup.listingGroupItems, setListingGroupValue, toSortedGroupItems]
  );

  const onMoveListingToGroup = useCallback(
    (targetListingGroupId: string) => {
      if (!targetListingId) {
        return;
      }
      const targetListing = listings.find(
        ({ listingId }) => listingId === targetListingId
      );
      if (!targetListing) {
        return;
      }

      const idToGroup = listingGroups.reduce(
        (res, group) => {
          res[group.listingGroupId] = group;
          return res;
        },
        {} as Record<string, MergeListingGroupInput>
      );

      const toGroup = idToGroup[targetListingGroupId];

      if (listingGroup.listingGroupId === UngroupedListingGroupId) {
        const newItems = [
          ...toGroup.listingGroupItems,
          {
            listingId: targetListing.id,
            priority: toGroup.listingGroupItems.length + 1,
            groupId: targetListing.ltGrpId,
            undercutSetting: null,
          } as ListingGroupItemInput,
        ];

        idToGroup[targetListingGroupId] = {
          ...toGroup,
          listingGroupItems: toSortedGroupItems(newItems),
        };
      } else {
        const fromGroup = idToGroup[listingGroup.listingGroupId];

        const targetListingGroupItem = fromGroup.listingGroupItems.find(
          ({ listingId }) => listingId === targetListingId
        );
        if (!targetListingGroupItem || !targetListing) {
          return;
        }

        idToGroup[listingGroup.listingGroupId] = {
          ...fromGroup,
          listingGroupItems: toSortedGroupItems(
            fromGroup.listingGroupItems.filter(
              ({ listingId }) => listingId !== targetListingId
            )
          ),
        };

        const targetGroupItems = [
          ...toGroup.listingGroupItems,
          { ...targetListingGroupItem, groupId: targetListing.ltGrpId },
        ];
        idToGroup[targetListingGroupId] = {
          ...toGroup,
          listingGroupItems: toSortedGroupItems(targetGroupItems),
        };
      }

      setValue('mergeListingGroupInputs', Object.values(idToGroup));
      moveListingGroupDialog.closeDialog();
    },
    [
      listingGroup.listingGroupId,
      listingGroups,
      listings,
      moveListingGroupDialog,
      setValue,
      targetListingId,
      toSortedGroupItems,
    ]
  );

  const columns = usePreviewTableColumns(
    listingGroup.desiredActiveListings,
    isUngroupedGroup,
    onRemoveListingFromGroup,
    onMoveListing,
    hasTableSortInGroupingWizardFeature
  );

  const onTableSortChange = useCallback(
    (sortingUpdaterFn: Updater<SortingState>) => {
      const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
      setSorting(newSortVal);

      if (
        newSortVal &&
        newSortVal.length &&
        !ColumnIdsAllowSorting.includes(
          newSortVal[0].id as GroupListingTableColumnId
        )
      ) {
        return;
      }
      const { id, desc } = newSortVal[0];
      const signFactor = desc ? -1 : 1;
      let updatedItems: ListingGroupItemInput[] = [];
      if (id === GroupListingTableColumnId.Section) {
        updatedItems = listingGroup.listingGroupItems.toSorted((a, b) => {
          const sectionA = idToListing[a.listingId]?.seating?.section ?? '';
          const sectionB = idToListing[b.listingId]?.seating?.section ?? '';
          return signFactor * sectionA.localeCompare(sectionB);
        });
      }

      if (id === GroupListingTableColumnId.Seats) {
        updatedItems = listingGroup.listingGroupItems.toSorted(
          (a, b) =>
            signFactor *
            sortSeatingSeats(
              idToListing[a.listingId]?.seating,
              idToListing[b.listingId].seating
            )
        );
      }

      if (id === GroupListingTableColumnId.AvailableQuantity) {
        updatedItems = listingGroup.listingGroupItems.toSorted((a, b) => {
          const quantityA = idToListing[a.listingId]?.availQty ?? 0;
          const quantityB = idToListing[b.listingId]?.availQty ?? 0;
          return signFactor * (quantityA - quantityB);
        });
      }

      if (id === GroupListingTableColumnId.DeliveryType) {
        updatedItems = listingGroup.listingGroupItems.toSorted((a, b) => {
          const typeA = idToListing[a.listingId]?.delivType ?? '';
          const typeB = idToListing[b.listingId]?.delivType ?? '';
          return signFactor * typeA.localeCompare(typeB);
        });
      }

      if (id === GroupListingTableColumnId.UnitCost) {
        updatedItems = listingGroup.listingGroupItems.toSorted((a, b) => {
          const costA = idToListing[a.listingId]?.unitCst?.amt ?? 0;
          const costB = idToListing[b.listingId]?.unitCst?.amt ?? 0;
          return signFactor * (costA - costB);
        });
      }
      // Sort rows by ordinal
      if (id === GroupListingTableColumnId.Row) {
        updatedItems = listingGroup.listingGroupItems.toSorted((a, b) => {
          const seatingA = idToListing[a.listingId]?.seating;
          const seatingB = idToListing[b.listingId]?.seating;
          const rowOrdinalA =
            !!rowIdToOrdinalMap && seatingA?.rowId != null
              ? (rowIdToOrdinalMap[seatingA?.rowId] ?? undefined)
              : undefined;

          const rowOrdinalB =
            !!rowIdToOrdinalMap && seatingB?.rowId != null
              ? (rowIdToOrdinalMap[seatingB?.rowId] ?? undefined)
              : undefined;

          // Before we sort by row ordinal we need to ensure ordinals exist for both listings.
          // If either are missing, we cannot compare a valid row ordinal to an undefined row ordinal.
          //
          // Additionally, we cannot a default value when the ordinal is missing, as that could
          // lead to incorrect sorting. We want to avoid a scenario where all listings without
          // a valid row ordinal mapping are clustered together in the final sort. In these
          // cases, we prefer to fallback to just sorting on the string value of the row name.
          const canSortByOrdinals =
            rowOrdinalA !== undefined && rowOrdinalB !== undefined;

          if (canSortByOrdinals) {
            return signFactor * (rowOrdinalA - rowOrdinalB);
          }

          const rowA = seatingA?.row ?? '';
          const rowB = seatingB?.row ?? '';
          return signFactor * rowA.localeCompare(rowB);
        });
      }

      if (!isEmpty(updatedItems)) {
        setListingGroupValue({
          listingGroupItems: toSortedGroupItems(
            updatedItems!.map((item, index) => ({
              ...item,
              priority: index + 1,
            }))
          ),
        });
      }
    },
    [
      idToListing,
      listingGroup.listingGroupItems,
      rowIdToOrdinalMap,
      setListingGroupValue,
      sorting,
      toSortedGroupItems,
    ]
  );

  const table = useReactTable({
    data: listings,
    columns,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row?.id + '',
    onSortingChange: onTableSortChange,
    sortingFns: {},
  });

  useEffect(() => {
    const listingById = listings.reduce(
      (res, item) => {
        res[item.id] = item;
        return res;
      },
      {} as Record<number, GroupListingTableItem>
    );

    const updatedGroupItem = table
      .getRowModel()
      .rows.map((row) => row.original?.id)
      .filter((id: number | undefined): id is number => id != null)
      .map((id) => listingById[id])
      .map((listing, index): ListingGroupItemInput => {
        return {
          listingId: listing.id,
          groupId: listing.ltGrpId,
          priority: index + 1,
          undercutSetting: null,
        };
      });

    setListingGroupValue({
      listingGroupItems: toSortedGroupItems(updatedGroupItem),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // reorder rows after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = listingIds.indexOf(active.id);
      const newIndex = listingIds.indexOf(over.id);
      const reordered = arrayMove(listings, oldIndex, newIndex);
      const updateGroupItems = reordered.map(
        (listing, index): ListingGroupItemInput => {
          const { undAbsAmt, undRelAmt } =
            listingGroup.groupUndercutSetting ?? {};
          const undercutSetting: ListingGroupUndercutSetting = {
            undAbsAmt: undAbsAmt ? undAbsAmt * index : null,
            undRelAmt: undRelAmt ? undRelAmt * index : null,
            actRankUndAbsAmt: null,
            actRankUndRelAmt: null,
          };
          return {
            listingId: listing.id,
            groupId: listing.ltGrpId,
            priority: index + 1,
            undercutSetting,
          };
        }
      );
      setSorting([]);
      setListingGroupValue({
        listingGroupItems: updateGroupItems,
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <table style={{ width: '100%' }}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <ColumnHeader key={header.id} header={header} table={table} />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            <SortableContext
              items={listingIds}
              strategy={verticalListSortingStrategy}
              key={new Date().getTime()}
            >
              {table.getRowModel().rows.map((row) => (
                <DraggableRow key={row.id} row={row} />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </DndContext>
      <MoveListingGroupDialog
        {...moveListingGroupDialog.dialogProps}
        currentListingGroupId={listingGroup.listingGroupId}
        listingGroups={listingGroups}
        listings={eventListings}
        onMoveListingToGroup={onMoveListingToGroup}
        onClose={moveListingGroupDialog.closeDialog}
      />
    </>
  );
};
